import { useParams, useNavigate } from 'react-router-dom';
import { Navbar } from '@/components/layout/Navbar';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Heart, Bookmark, Share2, MapPin, Calendar, Wallet, Sun } from 'lucide-react';
import { useState } from 'react';

const contents = [
  { id: '概述', text: '川西小环线是四川最经典的自驾线路之一，全程约1000公里，涵盖雪山、草原、海子等多样景观。' },
  { id: 'Day1', text: '成都出发，经雅安抵达康定。沿途可欣赏二郎山隧道、泸定桥等景点。晚上在康定住宿，感受藏族风情。' },
  { id: 'Day2', text: '康定出发前往新都桥，途经折多山。新都桥被誉为"摄影家的天堂"，光影变化万千。' },
  { id: 'Day3', text: '新都桥经塔公草原前往八美。塔公草原辽阔壮美，雅拉雪山巍峨耸立，是此行的高光时刻。' },
];

const related = [
  { id: 1, title: '西藏自驾游全攻略', dest: '西藏' },
  { id: 2, title: '云南香格里拉环线', dest: '云南' },
];

const GuideDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [liked, setLiked] = useState(false);
  const [collected, setCollected] = useState(false);
  const [activeSection, setActiveSection] = useState('概述');

  return (
    <div className="min-h-screen bg-bg-primary">
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 md:px-6 pt-24 pb-20">
        <div className="h-[240px] rounded-card overflow-hidden relative mb-6 shadow-card">
          <img src={`https://picsum.photos/seed/sichuan-mountain/1200/480`} alt="" className="w-full h-full mx-auto object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
          <Button variant="ghost" onClick={() => navigate('/guides')} className="absolute top-4 left-4 text-white hover:text-white/80"><ArrowLeft className="w-4 h-4 mr-1" /> 返回</Button>
          <h1 className="absolute bottom-6 left-6 text-white text-2xl font-bold">川西小环线深度自驾攻略</h1>
        </div>

        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary-light flex items-center justify-center text-primary font-bold">自</div>
            <div>
              <p className="text-sm font-medium text-text-primary">自驾达人</p>
              <div className="flex gap-2 mt-1">
                <Badge variant="secondary" className="bg-primary-light text-primary rounded-full text-xs"><MapPin className="w-3 h-3 mr-1" /> 四川</Badge>
                <Badge variant="secondary" className="bg-bg-secondary text-secondary rounded-full text-xs"><Calendar className="w-3 h-3 mr-1" /> 5天</Badge>
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className={`rounded-btn ${liked ? 'text-danger border-danger' : ''}`} onClick={() => setLiked(!liked)}><Heart className={`w-4 h-4 mr-1 ${liked ? 'fill-danger' : ''}`} /> 1.2k</Button>
            <Button variant="outline" size="sm" className={`rounded-btn ${collected ? 'text-primary border-primary' : ''}`} onClick={() => setCollected(!collected)}><Bookmark className={`w-4 h-4 mr-1 ${collected ? 'fill-primary' : ''}`} /></Button>
            <Button variant="outline" size="sm" className="rounded-btn"><Share2 className="w-4 h-4" /></Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <div className="lg:col-span-3">
            <Card className="border-0 shadow-card rounded-card bg-white mb-6">
              <CardContent className="p-6">
                <div className="flex gap-4 mb-6">
                  <div className="flex-1 bg-bg-secondary rounded-card p-4 text-center">
                    <Sun className="w-5 h-5 text-primary mx-auto mb-1" />
                    <p className="text-xs text-text-tertiary">最佳季节</p>
                    <p className="text-sm font-medium text-text-primary">6-10月</p>
                  </div>
                  <div className="flex-1 bg-bg-secondary rounded-card p-4 text-center">
                    <Wallet className="w-5 h-5 text-primary mx-auto mb-1" />
                    <p className="text-xs text-text-tertiary">预算范围</p>
                    <p className="text-sm font-medium text-text-primary">¥3000-5000</p>
                  </div>
                  <div className="flex-1 bg-bg-secondary rounded-card p-4 text-center">
                    <Calendar className="w-5 h-5 text-primary mx-auto mb-1" />
                    <p className="text-xs text-text-tertiary">推荐天数</p>
                    <p className="text-sm font-medium text-text-primary">5-7天</p>
                  </div>
                </div>
                <div className="prose prose-slate max-w-none text-text-primary leading-relaxed">
                  {contents.map((c) => (
                    <div key={c.id} id={c.id} className="mb-4">
                      <h3 className="text-lg font-bold text-text-primary mb-2">{c.id}</h3>
                      <p>{c.text}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="hidden lg:block">
            <div className="sticky top-28 bg-white rounded-card shadow-card p-4">
              <p className="text-sm font-medium text-text-primary mb-3">目录</p>
              <ul className="space-y-2">
                {contents.map((c) => (
                  <li key={c.id}><button onClick={() => { setActiveSection(c.id); document.getElementById(c.id)?.scrollIntoView({ behavior: 'smooth' }); }} className={`text-sm ${activeSection === c.id ? 'text-primary font-medium' : 'text-text-secondary'}`}>{c.id}</button></li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <div className="mt-8">
          <h2 className="text-lg font-bold text-text-primary mb-4">相关推荐</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {related.map((r) => (
              <Card key={r.id} className="border-0 shadow-card rounded-card bg-white cursor-pointer hover:shadow-hover transition-all" onClick={() => navigate(`/guide/${r.id}`)}>
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="w-16 h-16 rounded-smwidget bg-bg-secondary shrink-0 overflow-hidden">
                    <img src={`https://picsum.photos/seed/${r.dest}/200/200`} alt="" className="w-full h-full mx-auto object-cover" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-text-primary">{r.title}</p>
                    <p className="text-xs text-text-tertiary mt-1">{r.dest}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GuideDetail;
